import { Table } from 'antd';
import { ColumnsType, TableProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import NodataView from 'app/pages/NewAccount/components/NodataView';
import React, {
  CSSProperties,
  FC,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

interface CustomTableCSS {
  antTableContainer?: CSSProperties;
  antTableHeader?: CSSProperties;
  antTableBody?: CSSProperties;
  antTaleBody?: CSSProperties;
  th?: CSSProperties;
  td?: CSSProperties;
  footerCSS?: CSSProperties;
  tableCSS?: CSSProperties;
  antTableExpandedRowFixed?: CSSProperties;
}

type StyledTableProps = {
  dataSource?: readonly object[] | undefined;
  columns?: ColumnsType<object> | undefined;
  bordered?: boolean;
  onSelectChangeCallback?: (data: any[]) => void | undefined | Promise<void>;
  tableProps?: TableProps<any>;
  styleSlots?: CustomTableCSS;
  scroll?: any;
  pagination?: any;
  resetSelected?: boolean;
  emptyText?: ReactNode;
  selectedRowValue?: any[];
  tableRef?: any;
};

type Record = object & { checkBoxDisable?: boolean; hiddenCheckBox?: boolean };

const StyledTable: FC<StyledTableProps> = ({
  dataSource,
  columns,
  bordered,
  onSelectChangeCallback,
  tableProps,
  styleSlots,
  pagination = { hideOnSinglePage: true },
  resetSelected,
  emptyText,
  tableRef,
}) => {
  const translations = useSelector(selectTranslations);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const onSelectChange = (selectedRowsIds, selectedRows) => {
    setSelectedRowKeys(selectedRowsIds);
    onSelectChangeCallback && onSelectChangeCallback(selectedRows);
  };

  const rowSelection: TableRowSelection<object> = {
    selectedRowKeys,
    onChange: onSelectChange,
    renderCell(value, record: Record, index, originNode) {
      if (record?.hiddenCheckBox) return <div />;
      return originNode;
    },
    getCheckboxProps: (record: Record) => ({
      disabled: record.checkBoxDisable,
      key: record.checkBoxDisable,
    }),
  };
  useEffect(() => {
    if (resetSelected) {
      setSelectedRowKeys([]);
      onSelectChangeCallback && onSelectChangeCallback([]);
    }
  }, [onSelectChangeCallback, resetSelected]);

  return (
    <StyledTableWrapper
      ref={tableRef}
      locale={{
        emptyText: (
          <div
            style={{
              fontSize: 14,
              fontWeight: 400,
              color: '#6f8295',
              margin: 24,
            }}
          >
            {emptyText || translations?.['b2b.txt.no-result-found']}
          </div>
        ),
      }}
      {...styleSlots}
      dataSource={dataSource}
      columns={columns}
      bordered={bordered}
      {...(onSelectChangeCallback && { rowSelection })}
      pagination={pagination}
      scroll={{ x: 'max-content' }}
      {...tableProps}
    />
  );
};

export default StyledTable;

const StyledTableWrapper = styled(Table)<CustomTableCSS>`
  width: 100%;
  .ant-table-container {
    width: 100%;
    ${props => ({ ...props.antTableContainer })};
  }
  table {
    border: 1px solid #f0f0f3 !important;
    border-radius: 10px !important;
    overflow: hidden;
    ${props => ({ ...props.tableCSS })};
  }
  .ant-table-header {
    table {
      ${props => ({ ...props.antTableHeader })};
    }
  }
  .ant-table-body {
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: none;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #dbdbdb;
      border-radius: 5px;
    }

    &::-webkit-scrollbar {
      height: 8px;
    }

    ${props => ({ ...props.antTableBody })};

    table {
      ${props => ({ ...props.antTaleBody })};
    }
  }
  .ant-checkbox-wrapper {
    .ant-checkbox {
      top: 3px;
    }
  }
  .ant-table-tbody > tr:last-child > td {
    border: none !important;
  }
  .ant-table-tbody > tr:last-child > td {
    ${props => ({ ...props.footerCSS })}
  }
  .ant-table-container table > thead > tr:first-child th:first-child {
    border-top-left-radius: 10px;
  }
  .ant-table-container table > thead > tr:first-child th:last-child {
    border-top-right-radius: 10px;
  }
  .ant-table-thead {
    background-color: #fff1e3;
  }
  .ant-table-thead > tr > th {
    background-color: inherit;
    color: #ff7a00;
    padding: 15px 10px;
    text-align: center;
    font-weight: 600;
    ${props => ({ ...props.th })};
  }
  .ant-table-tbody > tr > td {
    background-color: inherit;
    padding: 15px 10px;
    text-align: center;
    color: black;
    ${props => ({ ...props.td })};
  }
  .ant-checkbox-indeterminate .ant-checkbox-inner::after {
    background-color: inherit;
    width: 10px;
    height: 10px;
    left: 50%;
  }
  .ant-table-tbody > tr.ant-table-row-selected:hover > td {
    background-color: #f8f9fa;
  }

  .ant-table-tbody .ant-table-placeholder .ant-table-expanded-row-fixed {
    ${props => ({ ...props.antTableExpandedRowFixed })};
  }

  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    border: none;
  }
  .ant-pagination-item {
    border-radius: 50%;
    border: 1px solid transparent;
    font-family: 'Be Vietnam Pro', sans-serif;
  }
  .ant-pagination-item-active,
  .ant-pagination-item:hover {
    border: 1px solid #e92127;
    color: #e92127;
  }
  .ant-table-pagination-right {
    padding-top: 20px;
    justify-content: center;
  }
  .ant-pagination-next,
  .ant-pagination-prev {
    line-height: 0px;
    color: #808080;
  }
  .ant-table-thead
    > tr
    > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
    width: 0px;
  }
  .ant-pagination-item-active a {
    color: #e92127;
  }
  .ant-table-content::-webkit-scrollbar {
    width: 8px;
  }

  .ant-table-content::-webkit-scrollbar-track {
    background: none;
  }

  .ant-table-content::-webkit-scrollbar-thumb {
    background-color: #dbdbdb;
    border-radius: 5px;
  }
  .ant-table-content::-webkit-scrollbar {
    height: 8px;
  }
  .ant-table-content {
    padding-bottom: 10px;
  }
  .ant-pagination-options {
    @media (max-width: 576px) {
      display: inline-block;
      margin-left: 4px;
    }
  }
  /* .ant-table-pagination.ant-pagination {
    position: relative;
    margin-top: 50px;
  }
  .ant-pagination-options {
    position: absolute;
    right: 0px;
    top: -32px;
  } */
`;
